import type { SpeechProvider } from '@xsai-ext/providers/utils'

import { createContext } from '@moeru/eventa/adapters/electron/renderer'
import { defineInvoke } from '@moeru/eventa'
import { errorMessageFrom } from '@moeru/std'
import { isElectronWindow, isStageTamagotchi } from '@proj-airi/stage-shared'
import {
  electronSherpaKokoroEnsureLoaded,
  electronSherpaKokoroSynthesize,
  sherpaKokoroDefaultVoiceId,
  sherpaKokoroModelId,
  sherpaKokoroVoices,
} from '@proj-airi/stage-shared/sherpa-kokoro'
import { z } from 'zod'

import { defineProvider } from '../registry'

function bytesFromBase64(value: string) {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++)
    bytes[index] = binary.charCodeAt(index)
  return bytes
}

function electronInvokes() {
  if (typeof window === 'undefined' || !isElectronWindow(window))
    throw new Error('Sherpa Kokoro requires the Electron desktop app.')

  const { context } = createContext(window.electron.ipcRenderer)
  return {
    ensureLoaded: defineInvoke(context, electronSherpaKokoroEnsureLoaded),
    synthesize: defineInvoke(context, electronSherpaKokoroSynthesize),
  }
}

export const providerSherpaKokoro = defineProvider({
  id: 'sherpa-kokoro',
  name: 'Kokoro Chinese (Local)',
  nameLocalize: ({ t }) => t('settings.pages.providers.provider.sherpa-kokoro.title'),
  description: 'Local Chinese and English speech with sherpa-onnx Kokoro.',
  descriptionLocalize: ({ t }) => t('settings.pages.providers.provider.sherpa-kokoro.description'),
  tasks: ['text-to-speech'],
  icon: 'i-lobe-icons:speaker',
  requiresCredentials: false,
  isAvailableBy: () => isStageTamagotchi(),
  createProviderConfig: () => z.object({
    model: z.string().default(sherpaKokoroModelId),
    voiceId: z.string().default(sherpaKokoroDefaultVoiceId),
  }),
  createProvider(): SpeechProvider {
    const invokes = electronInvokes()
    return {
      speech: () => ({
        baseURL: 'http://sherpa-kokoro/v1/',
        model: sherpaKokoroModelId,
        fetch: async (_input: RequestInfo | URL, init?: RequestInit) => {
          if (!init?.body || typeof init.body !== 'string')
            throw new Error('Invalid request body')
          const body = JSON.parse(init.body) as { input?: string, voice?: string, job_id?: string, turn_id?: string }
          if (!body.voice)
            throw new Error('Voice parameter is required')
          try {
            const result = await invokes.synthesize({
              text: body.input ?? '',
              voiceId: body.voice,
              ...(body.job_id
                ? { job: { jobId: body.job_id, ...(body.turn_id ? { turnId: body.turn_id } : {}) } }
                : {}),
            })
            return new Response(bytesFromBase64(result.wavBase64), {
              status: 200,
              headers: { 'Content-Type': 'audio/wav' },
            })
          }
          catch (error) {
            throw new Error(errorMessageFrom(error) || 'Sherpa Kokoro synthesis failed.')
          }
        },
      }),
    }
  },
  validationRequiredWhen: () => false,
  extraMethods: {
    listModels: async () => [{
      id: sherpaKokoroModelId,
      name: 'Kokoro multilingual v1.0',
      provider: 'sherpa-kokoro',
    }],
    loadModel: async () => {
      await electronInvokes().ensureLoaded()
    },
    listVoices: async () => sherpaKokoroVoices.map(voice => ({
      id: voice.id,
      name: `${voice.name} (${voice.gender}, Chinese)`,
      provider: 'sherpa-kokoro',
      languages: [{ code: voice.languageCode, title: voice.languageTitle }],
      gender: voice.gender,
    })),
  },
})
